import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const AvatarCreator = () => {
  const navigate = useNavigate();
  const [selectedBody, setSelectedBody] = useState(null);
  const [selectedFace, setSelectedFace] = useState(null);
  const [selectedEmotion, setSelectedEmotion] = useState(null);
  const currentDragType = useRef(null);
  
  // Positions for draggable elements within the room
  const [bodyPosition, setBodyPosition] = useState({ x: 35, y: 60 }); // percentage
  const [facePosition, setFacePosition] = useState({ x: 50, y: 12 }); // percentage relative to body
  const [emotionPosition, setEmotionPosition] = useState({ x: 50, y: 20 }); // percentage relative to face
  
  // Dragging state
  const [isDragging, setIsDragging] = useState({ body: false, face: false, emotion: false });
  const dragStartPos = useRef({ x: 0, y: 0 });
  const elementStartPos = useRef({ x: 0, y: 0 });

  // Generate arrays for body, face, and emotion options
  // Exclude bodies 5, 6, 7, 8, 9, 10, 11, and 12 (only bodies 1-4 available)
  const bodyOptions = Array.from({ length: 12 }, (_, i) => i + 1).filter(body => ![5, 6, 7, 8, 9, 10, 11, 12].includes(body));
  const faceOptions = Array.from({ length: 12 }, (_, i) => i + 1);
  const emotionOptions = Array.from({ length: 4 }, (_, i) => i + 1);

  const handleDragStart = (e, type, index) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ type, index }));
    e.dataTransfer.effectAllowed = 'move';
    currentDragType.current = type; // Store the drag type for dragOver checks
  };

  const handleDragOver = (e, dropType) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Only highlight if the dragged type matches the drop zone type
    if (currentDragType.current === dropType && e.currentTarget.style) {
      e.currentTarget.style.borderColor = '#37eb39';
      e.currentTarget.style.backgroundColor = 'rgba(55, 235, 57, 0.15)';
    }
  };


  const handleDrop = (e, dropType) => {
    e.preventDefault();
    try {
      const dataStr = e.dataTransfer.getData('application/json');
      if (!dataStr) return;
      
      const data = JSON.parse(dataStr);
      
      // Reset visual feedback
      if (e.currentTarget && e.currentTarget.style) {
        e.currentTarget.style.borderColor = '#ffa4f9';
        e.currentTarget.style.backgroundColor = 'rgba(255, 164, 249, 0.1)';
      }
      
      if (data && data.type === dropType) {
        const roomContainer = e.currentTarget.closest('[data-room-container]') || e.currentTarget;
        if (roomContainer) {
          const rect = roomContainer.getBoundingClientRect();
          const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
          const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
          
          if (data.type === 'body') {
            setSelectedBody(data.index);
            setBodyPosition({ x, y });
          } else if (data.type === 'face') {
            setSelectedFace(data.index);
            setFacePosition({ x, y });
          } else if (data.type === 'emotion') {
            setSelectedEmotion(data.index);
            setEmotionPosition({ x, y });
          }
        } else {
          // Fallback if room container not found
          if (data.type === 'body') {
            setSelectedBody(data.index);
          } else if (data.type === 'face') {
            setSelectedFace(data.index);
          } else if (data.type === 'emotion') {
            setSelectedEmotion(data.index);
          }
        }
      }
    } catch (err) {
      console.error('Error handling drop:', err);
    }
  };

  // Handlers for dragging within the room
  const handleMouseDown = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(prev => ({ ...prev, [type]: true }));
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    
    if (type === 'body') {
      elementStartPos.current = { x: bodyPosition.x, y: bodyPosition.y };
    } else if (type === 'face') {
      elementStartPos.current = { x: facePosition.x, y: facePosition.y };
    } else if (type === 'emotion') {
      elementStartPos.current = { x: emotionPosition.x, y: emotionPosition.y };
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging.body && !isDragging.face && !isDragging.emotion) return;
    
    const roomContainer = e.currentTarget;
    if (!roomContainer) return;
    
    const rect = roomContainer.getBoundingClientRect();
    const deltaX = ((e.clientX - dragStartPos.current.x) / rect.width) * 100;
    const deltaY = ((e.clientY - dragStartPos.current.y) / rect.height) * 100;
    
    if (isDragging.body) {
      setBodyPosition({
        x: Math.max(0, Math.min(100, elementStartPos.current.x + deltaX)),
        y: Math.max(0, Math.min(100, elementStartPos.current.y + deltaY))
      });
    } else if (isDragging.face) {
      setFacePosition({
        x: Math.max(0, Math.min(100, elementStartPos.current.x + deltaX)),
        y: Math.max(0, Math.min(100, elementStartPos.current.y + deltaY))
      });
    } else if (isDragging.emotion) {
      setEmotionPosition({
        x: Math.max(0, Math.min(100, elementStartPos.current.x + deltaX)),
        y: Math.max(0, Math.min(100, elementStartPos.current.y + deltaY))
      });
    }
  };

  const handleMouseUp = (e) => {
    e?.preventDefault();
    setIsDragging({ body: false, face: false, emotion: false });
  };

  const handleReset = () => {
    setSelectedBody(null);
    setSelectedFace(null);
    setSelectedEmotion(null);
    setBodyPosition({ x: 35, y: 60 });
    setFacePosition({ x: 50, y: 12 });
    setEmotionPosition({ x: 50, y: 20 });
    setIsDragging({ body: false, face: false, emotion: false });
  };

  const handleComplete = () => {
    if (selectedBody !== null && selectedFace !== null && selectedEmotion !== null) {
      // Save avatar to localStorage
      const avatarData = {
        body: selectedBody,
        face: selectedFace,
        emotion: selectedEmotion,
        positions: {
          body: bodyPosition,
          face: facePosition,
          emotion: emotionPosition
        }
      };
      localStorage.setItem('pariaAvatar', JSON.stringify(avatarData));
      
      // Navigate back to learning hub
      navigate('/dashboard/learning-hub');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      fontFamily: "'Fredoka', sans-serif",
      backgroundColor: '#ffa4f9',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        maxWidth: '1400px',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '28px',
        padding: '40px',
        boxShadow: '0 24px 60px rgba(0, 0, 0, 0.16)'
      }}>
        <h1 style={{
          fontSize: 'clamp(42px, 5vw, 56px)',
          fontWeight: '700',
          color: '#911ded',
          textAlign: 'center',
          marginBottom: '10px'
        }}>
          Create Your Avatar!
        </h1>
        <p style={{
          fontSize: 'clamp(20px, 2.5vw, 28px)',
          color: '#666',
          textAlign: 'center',
          marginBottom: '40px'
        }}>
          Choose your body, face, and emotion
        </p>

        {/* Three Selection Boxes */}
        <div className="avatar-selection-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '30px',
          marginBottom: '40px'
        }}>
          {/* Body Selection Box */}
          <div style={{
            border: '3px solid #ffa4f9',
            borderRadius: '20px',
            padding: '25px',
            backgroundColor: '#fff',
            minHeight: '500px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h2 style={{
              fontSize: 'clamp(24px, 3vw, 32px)',
              fontWeight: '700',
              color: '#DD2E44',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              Body Selection
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '20px',
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              padding: '10px',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              {bodyOptions.map((index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedBody(index);
                    // Set initial position if not already set
                    if (bodyPosition.x === 35 && bodyPosition.y === 60) {
                      setBodyPosition({ x: 35, y: 60 });
                    }
                  }}
                  style={{
                    border: selectedBody === index ? '4px solid #37eb39' : '3px solid #ffa4f9',
                    borderRadius: '12px',
                    padding: '12px',
                    backgroundColor: selectedBody === index ? '#f0fff0' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    width: '180px',
                    height: '240px',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedBody !== index) {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedBody !== index) {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  <img
                    src={`/body-${index}.png`}
                    alt={`Body ${index}`}
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, 'body', index)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      cursor: 'grab'
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.cursor = 'grab';
                      currentDragType.current = null;
                    }}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Face Selection Box */}
          <div style={{
            border: '3px solid #ffa4f9',
            borderRadius: '20px',
            padding: '25px',
            backgroundColor: '#fff',
            minHeight: '500px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h2 style={{
              fontSize: 'clamp(24px, 3vw, 32px)',
              fontWeight: '700',
              color: '#DD2E44',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              Face Selection
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              flex: 1,
              overflowY: 'auto',
              padding: '10px'
            }}>
              {faceOptions.map((index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedFace(index);
                    // Set initial position if not already set
                    if (facePosition.x === 50 && facePosition.y === 12) {
                      setFacePosition({ x: 50, y: 12 });
                    }
                  }}
                  style={{
                    border: selectedFace === index ? '4px solid #37eb39' : '3px solid #ffa4f9',
                    borderRadius: '12px',
                    padding: '8px',
                    backgroundColor: selectedFace === index ? '#f0fff0' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    aspectRatio: '1 / 1',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedFace !== index) {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedFace !== index) {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  <img
                    src={`/face-${index}.png`}
                    alt={`Face ${index}`}
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, 'face', index)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      cursor: 'grab'
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.cursor = 'grab';
                      currentDragType.current = null;
                    }}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Emotion Selection Box */}
          <div style={{
            border: '3px solid #ffa4f9',
            borderRadius: '20px',
            padding: '25px',
            backgroundColor: '#fff',
            minHeight: '500px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h2 style={{
              fontSize: 'clamp(24px, 3vw, 32px)',
              fontWeight: '700',
              color: '#DD2E44',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              Emotion Selection
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              flex: 1,
              padding: '10px',
              alignContent: 'start'
            }}>
              {emotionOptions.map((index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedEmotion(index);
                    // Set initial position if not already set
                    if (emotionPosition.x === 50 && emotionPosition.y === 20) {
                      setEmotionPosition({ x: 50, y: 20 });
                    }
                  }}
                  style={{
                    border: selectedEmotion === index ? '4px solid #37eb39' : '3px solid #ffa4f9',
                    borderRadius: '12px',
                    padding: '8px',
                    backgroundColor: selectedEmotion === index ? '#f0fff0' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    aspectRatio: '1 / 1',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedEmotion !== index) {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedEmotion !== index) {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  <img
                    src={`/emotion-${index}.png`}
                    alt={`Emotion ${index}`}
                    draggable="true"
                    onDragStart={(e) => handleDragStart(e, 'emotion', index)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      cursor: 'grab'
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.cursor = 'grab';
                      currentDragType.current = null;
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Preview */}
        <div style={{
          textAlign: 'center',
          marginBottom: '30px',
          padding: '20px',
          backgroundColor: '#f9f9f9',
          borderRadius: '20px'
        }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px',
              flexWrap: 'wrap',
              gap: '15px'
            }}>
              <h3 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#DD2E44',
                margin: 0
              }}>
                Your Avatar Preview
              </h3>
              <button
                onClick={handleReset}
                style={{
                  border: '2px solid #ffa4f9',
                  borderRadius: '12px',
                  padding: '10px 20px',
                  fontFamily: "'Fredoka', sans-serif",
                  fontWeight: '600',
                  fontSize: '16px',
                  color: '#ffa4f9',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#ffa4f9';
                  e.target.style.color = 'white';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.color = '#ffa4f9';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                }}
              >
                Reset
              </button>
            </div>
            {/* Room Canvas */}
            <div 
              data-room-container
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onDragOver={(e) => {
                e.preventDefault();
                handleDragOver(e, 'body');
                handleDragOver(e, 'face');
                handleDragOver(e, 'emotion');
              }}
              onDrop={(e) => {
                try {
                  const dataStr = e.dataTransfer.getData('application/json');
                  if (dataStr) {
                    const data = JSON.parse(dataStr);
                    if (data.type === 'body') handleDrop(e, 'body');
                    else if (data.type === 'face') handleDrop(e, 'face');
                    else if (data.type === 'emotion') handleDrop(e, 'emotion');
                  }
                } catch (err) {
                  console.error('Error parsing drop data:', err);
                }
              }}
              style={{
                position: 'relative',
                width: '100%',
                maxWidth: '800px',
                margin: '0 auto',
                borderRadius: '16px',
                overflow: 'visible',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                backgroundColor: '#f0f0f0',
                minHeight: '400px',
                cursor: (isDragging.body || isDragging.face || isDragging.emotion) ? 'grabbing' : 'default'
              }}
            >
              {/* Room Background */}
              <img
                src="/Room.png"
                alt="Room"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  zIndex: 0,
                  pointerEvents: 'none'
                }}
              />
              
              {/* Draggable Body */}
              {selectedBody && (
                <img
                  src={`/body-${selectedBody}.png`}
                  alt="Body"
                  draggable="false"
                  onMouseDown={(e) => handleMouseDown(e, 'body')}
                  style={{
                    position: 'absolute',
                    top: `${bodyPosition.y}%`,
                    left: `${bodyPosition.x}%`,
                    transform: 'translate(-50%, -50%)',
                    width: '200px',
                    height: 'auto',
                    maxHeight: '280px',
                    objectFit: 'contain',
                    zIndex: 1,
                    cursor: 'grab',
                    userSelect: 'none',
                    pointerEvents: 'auto',
                    filter: isDragging.body ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' : 'none',
                    transition: isDragging.body ? 'none' : 'filter 0.2s ease'
                  }}
                />
              )}
              
              {/* Draggable Face */}
              {selectedFace && (
                <img
                  src={`/face-${selectedFace}.png`}
                  alt="Face"
                  draggable="false"
                  onMouseDown={(e) => handleMouseDown(e, 'face')}
                  style={{
                    position: 'absolute',
                    top: `${facePosition.y}%`,
                    left: `${facePosition.x}%`,
                    transform: 'translate(-50%, -50%)',
                    width: '100px',
                    height: 'auto',
                    maxHeight: '140px',
                    objectFit: 'contain',
                    zIndex: 2,
                    cursor: 'grab',
                    userSelect: 'none',
                    pointerEvents: 'auto',
                    filter: isDragging.face ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' : 'none',
                    transition: isDragging.face ? 'none' : 'filter 0.2s ease'
                  }}
                />
              )}
              
              {/* Draggable Emotion */}
              {selectedEmotion && (
                <img
                  src={`/emotion-${selectedEmotion}.png`}
                  alt="Emotion"
                  draggable="false"
                  onMouseDown={(e) => handleMouseDown(e, 'emotion')}
                  style={{
                    position: 'absolute',
                    top: `${emotionPosition.y}%`,
                    left: `${emotionPosition.x}%`,
                    transform: 'translate(-50%, -50%)',
                    width: '80px',
                    height: 'auto',
                    maxHeight: '112px',
                    objectFit: 'contain',
                    zIndex: 3,
                    cursor: 'grab',
                    userSelect: 'none',
                    pointerEvents: 'auto',
                    filter: isDragging.emotion ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' : 'none',
                    transition: isDragging.emotion ? 'none' : 'filter 0.2s ease'
                  }}
                />
              )}
            </div>
          </div>

        {/* Complete Button */}
        <div style={{ textAlign: 'center', display: 'flex', gap: '20px', justifyContent: 'center' }}>
          <button
            onClick={() => navigate('/dashboard/learning-hub')}
            style={{
              border: 'none',
              borderRadius: '999px',
              padding: '14px 30px',
              fontFamily: "'Fredoka', sans-serif",
              fontWeight: '700',
              fontSize: 'clamp(20px, 2.5vw, 28px)',
              color: '#666',
              background: '#f0f0f0',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#e0e0e0';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#f0f0f0';
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleComplete}
            disabled={selectedBody === null || selectedFace === null || selectedEmotion === null}
            style={{
              border: 'none',
              borderRadius: '999px',
              padding: '18px 50px',
              fontFamily: "'Fredoka', sans-serif",
              fontWeight: '700',
              fontSize: 'clamp(28px, 3.5vw, 36px)',
              color: '#ffffff',
              background: selectedBody !== null && selectedFace !== null && selectedEmotion !== null
                ? 'linear-gradient(135deg, #5AFF5C 0%, #37eb39 55%, #1FA521 100%)'
                : '#ccc',
              boxShadow: selectedBody !== null && selectedFace !== null && selectedEmotion !== null
                ? '0 14px 26px rgba(55, 235, 57, 0.35), 0 4px 10px rgba(0, 0, 0, 0.14)'
                : 'none',
              cursor: selectedBody !== null && selectedFace !== null && selectedEmotion !== null ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              opacity: selectedBody !== null && selectedFace !== null && selectedEmotion !== null ? 1 : 0.6
            }}
            onMouseEnter={(e) => {
              if (selectedBody !== null && selectedFace !== null && selectedEmotion !== null) {
                e.target.style.transform = 'translateY(1px)';
                e.target.style.boxShadow = '0 10px 20px rgba(55, 235, 57, 0.3), 0 3px 8px rgba(0, 0, 0, 0.12)';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedBody !== null && selectedFace !== null && selectedEmotion !== null) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 14px 26px rgba(55, 235, 57, 0.35), 0 4px 10px rgba(0, 0, 0, 0.14)';
              }
            }}
          >
            {selectedBody !== null && selectedFace !== null && selectedEmotion !== null ? 'Save Avatar!' : 'Select All Options'}
          </button>
        </div>
      </div>
      <style>{`
        @media (max-width: 1024px) {
          .avatar-selection-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AvatarCreator;
